import { GridType, IGridState, IRangeSliderValue } from "@infragistics/igniteui-angular";
import { Model } from "../models/model";

/**
 * Applies provided grid state to the provided grid. This method restores only
 * column width and column position.
 * @param state Grid state to restore
 * @param grid Grid where the state should be applied
 */
export function restoreState(state: IGridState, grid: GridType): void {
  state.columns?.forEach((c, i) => {
    const column = grid.columnList.find(col => col.field === c.field);
    if (!column) return;
    column.width = c.width;
    column.move(i);
  });
  state.sorting?.forEach(s => grid.sort(s));
}

/**
 * Converts date to string representing the date ready to be saved in the DB
 * @param input Date object
 * @returns String representing provided date or undefined
 */
export function dateToDB(input: string | undefined): string | undefined {
  if (input == null) return undefined;

  const date = new Date(input);
  const zonelessDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  const result = zonelessDate.toISOString().split('T')[0]
  return result;
}

/**
 * Convert backend data to Date object
 * @param input Date from backend as saved in the DB
 * @returns Date object or undefined
 */
export function dateToView(input: string | Date | undefined): Date | undefined {
  if (input == null) return undefined;
  return new Date(input);
}

/**
 * Converts date to string representing the date ready to be saved in the DB
 * @param input Date object
 * @returns String representing provided date or undefined
 */
export function timeToDB(input: string | undefined): string | undefined {
  if (input == null) return undefined;

  const date = new Date(input);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const result = hours*100 + minutes;
  return result.toString();
}

/**
 * Convert backend data to Date object with correct hours and minutes
 * @param input Date from backend as saved in the DB
 * @returns Date object with correct hours and minutes or undefined
 */
export function timeToView(input: string | undefined): Date | undefined {
  if (input == null) return undefined;
  const date = new Date();
  const hours = +input.slice(0, -2);
  const minutes = +input.slice(-2);
  date.setHours(hours);
  date.setMinutes(minutes);
  return date;
}

/**
 * Converts range slider value to min and max for DB
 * @param input Range slider value to convert to min and max values
 * @returns Object with min and max values ready for DB save
 */
export function ageRangeToDB(input: IRangeSliderValue): { minAge: number | undefined, maxAge: number | undefined } {
  const result: { minAge: number | undefined, maxAge: number | undefined } = { minAge: undefined, maxAge: undefined };
  if (input.lower != null) result.minAge = input.lower;
  if (input.upper != null) result.maxAge = input.upper;
  return result;
}
/**
 * Converts min and max value from DB to range slider value
 * @param min Min value to apply to range lower
 * @param max Max value to apply to range upper
 * @returns Range slider value 
 */
export function ageRangeToView(min: number | undefined, max: number | undefined): IRangeSliderValue {
  const result: IRangeSliderValue = {
    lower: min ?? 0,
    upper: max ?? 0,
  };
  return result;
}

/**
 * Update the provided collection with provided item
 * @param item Item to add or update in the collection
 * @param items Collection to add provided item to. If item with same id exists item will be replaced with provided one
 * @returns Updated collection
 */
export function updateCollection<T extends Model>(item: T | undefined, items: T[]): T[] {
  if (item == null || item.id == null)
    return items;
  const index = items.findIndex(i => i.id === item.id);
  if (index < 0) {
    items.push(item);
  } else {
    items.splice(index, 1, item);
  }
  items = [...items];
  return items;
}
